// frontend/src/pages/Customer/EditCustomer.jsx
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Save,
  User,
  UserRoundPen,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Customers.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ ROBUST: ALWAYS returns a 3‑letter currency code as a string
const getCompanyCurrency = (company) => {
  if (!company) return "USD";

  let currency = null;

  // 1. Try company.currency (could be object or string)
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  // 2. Try company.currency_code
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  // 3. Try company.base_currency
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  // Final safety: default to USD if not a valid 3-letter code
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

export default function EditCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const { getCurrencySymbol } = useCurrency();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [customer, setCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_type: "regular",
    phone: "",
    email: "",
    address: "",
    currency_code: companyCurrency, // always company currency
    credit_limit: "0.00",
    status: "active",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const response = await axios.get(`${url}/api/customer/${id}`, {
          headers: getAuthHeaders(),
        });
        const item = response.data?.data;
        if (!active) return;

        setCustomer(item);
        setFormData({
          customer_name: item?.customer_name || "",
          customer_type: item?.customer_type || "regular",
          phone: item?.phone || "",
          email: item?.email || "",
          address: item?.address || "",
          // ✅ Force to company currency, ignore stored value
          currency_code: companyCurrency,
          credit_limit: String(item?.credit_limit ?? 0),
          status: item?.status || "active",
        });
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load customer");
        navigate("/ViewCustomer");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id, navigate, companyCurrency]);

  const setField = (name, value) => {
    setFormData((current) => {
      const next = { ...current, [name]: value };
      if (name === "customer_type" && value === "walk_in") {
        next.credit_limit = "0.00";
      }
      return next;
    });
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const name = formData.customer_name.trim();
    const creditLimit = Number(formData.credit_limit || 0);

    if (name.length < 2 || name.length > 100) {
      nextErrors.customer_name = "Customer name must contain 2 to 100 characters";
    }
    if (formData.phone && !/^[\d\s+()\-]{7,30}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number";
    }
    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      nextErrors.email = "Enter a valid email address";
    }
    // ✅ Currency validation still present but field is disabled
    if (!/^[A-Za-z]{3}$/.test(formData.currency_code)) {
      nextErrors.currency_code = "Use a three-letter currency code";
    }
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      nextErrors.credit_limit = "Credit limit must be zero or greater";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.put(
        `${url}/api/customer/${id}`,
        {
          customer_name: formData.customer_name.trim(),
          customer_type: formData.customer_type,
          phone: formData.phone.trim(),
          email: formData.email.trim().toLowerCase(),
          address: formData.address.trim(),
          // ✅ Always send company currency (ignore what user might have typed)
          currency_code: formData.currency_code.trim().toUpperCase(),
          credit_limit:
            formData.customer_type === "walk_in"
              ? 0
              : Number(formData.credit_limit || 0),
          status: formData.status,
        },
        { headers: getAuthHeaders() },
      );

      toast.success(response.data?.message || "Customer updated successfully");
      window.setTimeout(() => navigate("/ViewCustomer"), 400);
    } catch (error) {
      const response = error.response?.data;
      toast.error(response?.message || "Failed to update customer");
      if (Array.isArray(response?.errors)) {
        setErrors((current) => ({
          ...current,
          ...Object.fromEntries(response.errors.map((item) => [item.field, item.message])),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="customer-page-shell">
        <div className="customer-loading-card">
          <Loader2 className="customer-spin" size={30} />
          <span>Loading customer…</span>
        </div>
      </main>
    );
  }

  if (!customer) return null;

  return (
    <main className="customer-page-shell">
      <section className="customer-form-card">
        <button
          type="button"
          className="customer-close-button"
          onClick={() => navigate("/ViewCustomer")}
          aria-label="Close customer editor"
        >
          <X size={20} />
        </button>

        <header className="customer-form-header">
          <div className="customer-form-icon"><UserRoundPen size={24} /></div>
          <div>
            <p className="customer-eyebrow">Customer master</p>
            <h1>Edit customer</h1>
            <p>Update contact, type, limit and status.</p>
          </div>
        </header>

        <form className="customer-form" onSubmit={handleSubmit} noValidate>
          <div className="customer-field customer-field--full">
            <label htmlFor="edit-customer-name"><User size={15} /> Customer name *</label>
            <input
              id="edit-customer-name"
              value={formData.customer_name}
              onChange={(event) => setField("customer_name", event.target.value)}
              maxLength={100}
            />
            {errors.customer_name && <small className="customer-error">{errors.customer_name}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="edit-customer-type"><User size={15} /> Customer type</label>
            <select
              id="edit-customer-type"
              value={formData.customer_type}
              onChange={(event) => setField("customer_type", event.target.value)}
            >
              <option value="walk_in">Walk In</option>
              <option value="regular">Regular</option>
              <option value="credit">Credit</option>
            </select>
            {errors.customer_type && <small className="customer-error">{errors.customer_type}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="edit-customer-phone"><Phone size={15} /> Phone</label>
            <input
              id="edit-customer-phone"
              value={formData.phone}
              onChange={(event) => setField("phone", event.target.value)}
              maxLength={30}
            />
            {errors.phone && <small className="customer-error">{errors.phone}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="edit-customer-email"><Mail size={15} /> Email</label>
            <input
              id="edit-customer-email"
              type="email"
              value={formData.email}
              onChange={(event) => setField("email", event.target.value)}
              maxLength={120}
            />
            <small className="customer-hint">Duplicate customer emails are allowed.</small>
            {errors.email && <small className="customer-error">{errors.email}</small>}
          </div>

          {/* ✅ Currency field – disabled and auto-filled with company currency */}
          <div className="customer-field">
            <label htmlFor="edit-customer-currency"><CreditCard size={15} /> Currency</label>
            <input
              id="edit-customer-currency"
              value={formData.currency_code}
              disabled
              style={{
                background: '#f5f5f5',
                cursor: 'not-allowed',
                opacity: 0.8,
              }}
            />
            <small style={{ color: '#666' }}>
              Currency is set to the company's base currency and cannot be changed.
            </small>
            {errors.currency_code && <small className="customer-error">{errors.currency_code}</small>}
          </div>

          <div className="customer-field customer-field--full">
            <label htmlFor="edit-customer-address"><MapPin size={15} /> Address</label>
            <input
              id="edit-customer-address"
              value={formData.address}
              onChange={(event) => setField("address", event.target.value)}
              maxLength={250}
            />
          </div>

          <div className="customer-field">
            <label htmlFor="edit-customer-credit-limit"><CreditCard size={15} /> Credit limit</label>
            <input
              id="edit-customer-credit-limit"
              type="number"
              min="0"
              step="0.01"
              value={formData.credit_limit}
              onChange={(event) => setField("credit_limit", event.target.value)}
              disabled={formData.customer_type === "walk_in"}
            />
            {errors.credit_limit && <small className="customer-error">{errors.credit_limit}</small>}
          </div>

          <div className="customer-field">
            <label htmlFor="edit-customer-status"><AlertCircle size={15} /> Status</label>
            <select
              id="edit-customer-status"
              value={formData.status}
              onChange={(event) => setField("status", event.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <small className="customer-hint">
              Use Active to reactivate a previously deactivated customer.
            </small>
          </div>

          <div className="customer-form-actions customer-field--full">
            <button
              type="button"
              className="customer-button customer-button--secondary"
              onClick={() => navigate("/ViewCustomer")}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="customer-button customer-button--primary"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="customer-spin" size={17} /> : <Save size={17} />}
              {submitting ? "Updating…" : "Update customer"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}